/**
 * Customers Page - 고객사 관리
 *
 * 고객사(테넌트 하위 조직) 목록을 조회/등록/수정/삭제하는 화면.
 * "팀 수" 컬럼 클릭 시 해당 고객사의 팀 CRUD 팝업 모달 표시.
 */

import { useState, useEffect, useCallback } from 'react';
import { Topbar, PanelTitle } from '../components/layout';
import { Button, Modal, Select, SearchInput, SortableTh, useTableSort, useToast } from '../components/ui';
import { authApi, customerApi, tenantApi, teamApi, Customer, Tenant, Team } from '../api';
import { isProductAdminRole, isTenantAdminRole, isCustomerAdminRole } from '../utils/role';
import EditIcon from '../assets/icons/common/edit.svg?react';
import TrashIcon from '../assets/icons/common/trash.svg?react';
import '../styles/adminPage.css';

export function CustomersPage({ embedded = false }: { embedded?: boolean }) {
  const { showToast } = useToast();

  const currentRole = authApi.getUser()?.role;
  const canManageCustomers = isProductAdminRole(currentRole) || isTenantAdminRole(currentRole);
  const canManageTeams =
    isProductAdminRole(currentRole) ||
    isTenantAdminRole(currentRole) ||
    isCustomerAdminRole(currentRole);
  const isProductAdmin = isProductAdminRole(currentRole);

  // ── 고객사 ──────────────────────────────────────────────────
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [tenantFilter, setTenantFilter] = useState('');
  const sort = useTableSort<'name' | 'code' | 'tenantName' | 'status' | 'createdAt'>();

  // 고객사 수정 모달
  const [selected, setSelected] = useState<Customer | null>(null);
  const [editForm, setEditForm] = useState({ name: '', code: '', description: '', tenantId: '', status: 'active' as 'active' | 'inactive' });
  const [saving, setSaving] = useState(false);

  // 고객사 등록 모달
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', code: '', description: '', tenantId: '', status: 'active' as 'active' | 'inactive' });
  const [addErrors, setAddErrors] = useState<Set<string>>(new Set());
  const [adding, setAdding] = useState(false);

  // 고객사 삭제 확인
  const [deleteConfirm, setDeleteConfirm] = useState<{ id: string; name: string } | null>(null);
  const [deleting, setDeleting] = useState(false);

  // ── 팀 수 캐시 ─────────────────────────────────────────────
  const [teamCounts, setTeamCounts] = useState<Record<string, number>>({});

  // ── 팀 CRUD 모달 ───────────────────────────────────────────
  const [teamModalCustomer, setTeamModalCustomer] = useState<Customer | null>(null);
  const [teams, setTeams] = useState<Team[]>([]);
  const [teamsLoading, setTeamsLoading] = useState(false);

  // 팀 등록 폼
  const [showTeamAddForm, setShowTeamAddForm] = useState(false);
  const [teamAddForm, setTeamAddForm] = useState({ name: '', description: '' });
  const [teamAdding, setTeamAdding] = useState(false);
  const [teamAddErrors, setTeamAddErrors] = useState<Set<string>>(new Set());

  // 팀 수정 (인라인)
  const [editingTeamId, setEditingTeamId] = useState<string | null>(null);
  const [teamEditForm, setTeamEditForm] = useState({ name: '', description: '' });
  const [teamSaving, setTeamSaving] = useState(false);

  // 팀 삭제 확인
  const [teamDeleteConfirm, setTeamDeleteConfirm] = useState<{ id: string; name: string } | null>(null);
  const [teamDeleting, setTeamDeleting] = useState(false);

  // ── 데이터 로드 ────────────────────────────────────────────
  useEffect(() => {
    loadTenants();
    loadCustomers();
  }, []);

  const loadTenants = async () => {
    try {
      const data = await tenantApi.list();
      setTenants(data);
    } catch { /* 드롭다운만 비게 됨 */ }
  };

  const loadCustomers = async () => {
    try {
      setLoading(true);
      const data = await customerApi.list();
      setCustomers(data);
      // 팀 수 일괄 로드
      loadTeamCounts(data);
    } catch {
      showToast('error', '고객사 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const loadTeamCounts = useCallback(async (list: Customer[]) => {
    const counts: Record<string, number> = {};
    await Promise.allSettled(
      list.map(async (c) => {
        try {
          const teamList = await teamApi.listByCustomer(c.id);
          counts[c.id] = teamList.length;
        } catch {
          counts[c.id] = 0;
        }
      })
    );
    setTeamCounts(counts);
  }, []);

  // ── 팀 모달 열기/닫기 ─────────────────────────────────────
  const openTeamModal = async (c: Customer) => {
    setTeamModalCustomer(c);
    setShowTeamAddForm(false);
    setTeamAddForm({ name: '', description: '' });
    setTeamAddErrors(new Set());
    setEditingTeamId(null);
    setTeamsLoading(true);
    try {
      const data = await teamApi.listByCustomer(c.id);
      setTeams(data);
    } catch {
      showToast('error', '팀 목록을 불러오는데 실패했습니다.');
    } finally {
      setTeamsLoading(false);
    }
  };

  const closeTeamModal = () => {
    setTeamModalCustomer(null);
    setTeams([]);
    setShowTeamAddForm(false);
    setEditingTeamId(null);
  };

  // ── 팀 등록 ───────────────────────────────────────────────
  const handleTeamAdd = async () => {
    if (!teamModalCustomer) return;
    const errs = new Set<string>();
    if (!teamAddForm.name.trim()) errs.add('name');
    if (errs.size > 0) { setTeamAddErrors(errs); return; }
    setTeamAddErrors(new Set());
    try {
      setTeamAdding(true);
      const created = await teamApi.create({
        name: teamAddForm.name.trim(),
        description: teamAddForm.description.trim(),
        customerId: teamModalCustomer.id,
        tenantId: teamModalCustomer.tenantId,
      });
      setTeams((prev) => [...prev, created]);
      setTeamCounts((prev) => ({ ...prev, [teamModalCustomer.id]: (prev[teamModalCustomer.id] ?? 0) + 1 }));
      setTeamAddForm({ name: '', description: '' });
      setShowTeamAddForm(false);
      showToast('success', '팀이 등록되었습니다.');
    } catch {
      showToast('error', '팀 등록에 실패했습니다.');
    } finally {
      setTeamAdding(false);
    }
  };

  // ── 팀 수정 ───────────────────────────────────────────────
  const startTeamEdit = (team: Team) => {
    setEditingTeamId(team.id);
    setTeamEditForm({ name: team.name, description: team.description || '' });
  };

  const cancelTeamEdit = () => {
    setEditingTeamId(null);
    setTeamEditForm({ name: '', description: '' });
  };

  const handleTeamSave = async (teamId: string) => {
    if (!teamEditForm.name.trim()) {
      showToast('error', '팀 이름을 입력해주세요.');
      return;
    }
    try {
      setTeamSaving(true);
      const updated = await teamApi.update(teamId, {
        name: teamEditForm.name.trim(),
        description: teamEditForm.description.trim(),
      });
      setTeams((prev) => prev.map((t) => (t.id === teamId ? updated : t)));
      setEditingTeamId(null);
      showToast('success', '팀 정보가 수정되었습니다.');
    } catch {
      showToast('error', '팀 수정에 실패했습니다.');
    } finally {
      setTeamSaving(false);
    }
  };

  // ── 팀 삭제 ───────────────────────────────────────────────
  const handleTeamDelete = (team: Team) => {
    setTeamDeleteConfirm({ id: team.id, name: team.name });
  };

  const confirmTeamDelete = async () => {
    if (!teamDeleteConfirm || !teamModalCustomer) return;
    try {
      setTeamDeleting(true);
      await teamApi.remove(teamDeleteConfirm.id);
      setTeams((prev) => prev.filter((t) => t.id !== teamDeleteConfirm.id));
      setTeamCounts((prev) => ({
        ...prev,
        [teamModalCustomer.id]: Math.max(0, (prev[teamModalCustomer.id] ?? 1) - 1),
      }));
      showToast('success', '팀이 삭제되었습니다.');
    } catch {
      showToast('error', '팀 삭제에 실패했습니다.');
    } finally {
      setTeamDeleting(false);
      setTeamDeleteConfirm(null);
    }
  };

  // ── 고객사 CRUD ────────────────────────────────────────────
  const filtered = customers.filter((c) => {
    if (tenantFilter && c.tenantId !== tenantFilter) return false;
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      (c.name || '').toLowerCase().includes(q) ||
      (c.code || '').toLowerCase().includes(q) ||
      (c.tenantName || '').toLowerCase().includes(q) ||
      (c.description || '').toLowerCase().includes(q)
    );
  });

  const highlightText = (text: string) => {
    if (!searchQuery.trim() || !text) return text;
    const q = searchQuery.toLowerCase();
    const idx = text.toLowerCase().indexOf(q);
    if (idx === -1) return text;
    return (
      <>
        {text.slice(0, idx)}
        <span className="search-highlight">{text.slice(idx, idx + q.length)}</span>
        {text.slice(idx + q.length)}
      </>
    );
  };

  const openAddModal = () => {
    if (tenants.length === 0) {
      showToast('error', '먼저 테넌트를 등록해주세요. 고객사는 테넌트에 소속됩니다.');
      return;
    }
    setAddForm({ name: '', code: '', description: '', tenantId: tenants[0]?.id || '', status: 'active' });
    setShowAddModal(true);
  };

  const openDetail = (c: Customer) => {
    setSelected(c);
    setEditForm({ name: c.name, code: c.code, description: c.description || '', tenantId: c.tenantId, status: c.status });
  };

  const closeDetail = () => {
    setSelected(null);
    setEditForm({ name: '', code: '', description: '', tenantId: '', status: 'active' });
  };

  const handleSave = async () => {
    if (!selected) return;
    if (!editForm.name.trim() || !editForm.code.trim()) {
      showToast('error', '이름과 코드는 필수입니다.');
      return;
    }
    if (!editForm.tenantId) {
      showToast('error', '소속 테넌트를 선택해주세요.');
      return;
    }
    try {
      setSaving(true);
      const updated = await customerApi.update(selected.id, editForm);
      const tenantName = tenants.find((t) => t.id === updated.tenantId)?.name;
      setCustomers((prev) => prev.map((c) => (c.id === selected.id ? { ...c, ...updated, tenantName } : c)));
      showToast('success', '고객사 정보가 수정되었습니다.');
      closeDetail();
    } catch {
      showToast('error', '수정에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const closeAddModal = () => {
    setShowAddModal(false);
    setAddForm({ name: '', code: '', description: '', tenantId: '', status: 'active' });
    setAddErrors(new Set());
  };

  const clearAddError = (field: string) => {
    setAddErrors((prev) => {
      if (!prev.has(field)) return prev;
      const next = new Set(prev);
      next.delete(field);
      return next;
    });
  };

  const handleAdd = async () => {
    const errs = new Set<string>();
    if (!addForm.tenantId) errs.add('tenantId');
    if (!addForm.name.trim()) errs.add('name');
    if (!addForm.code.trim()) errs.add('code');
    if (errs.size > 0) { setAddErrors(errs); return; }
    setAddErrors(new Set());
    try {
      setAdding(true);
      const created = await customerApi.create(addForm);
      const tenantName = tenants.find((t) => t.id === created.tenantId)?.name;
      setCustomers((prev) => [...prev, { ...created, tenantName }]);
      setTeamCounts((prev) => ({ ...prev, [created.id]: 0 }));
      showToast('success', '고객사가 등록되었습니다.');
      closeAddModal();
    } catch {
      showToast('error', '등록에 실패했습니다.');
    } finally {
      setAdding(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    try {
      setDeleting(true);
      await customerApi.remove(deleteConfirm.id);
      setCustomers((prev) => prev.filter((c) => c.id !== deleteConfirm.id));
      showToast('success', '고객사가 삭제되었습니다.');
    } catch {
      showToast('error', '삭제에 실패했습니다.');
    } finally {
      setDeleting(false);
      setDeleteConfirm(null);
    }
  };

  // ── 검색바 ─────────────────────────────────────────────────
  const searchBar = (
    <div className="users-search-bar">
      <SearchInput
        placeholder="이름, 코드, 테넌트로 검색..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        onClear={() => setSearchQuery('')}
      />
      <button className="users-search-refresh" onClick={loadCustomers} disabled={loading} title="새로고침">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8" />
          <path d="M21 3v5h-5" />
        </svg>
      </button>
    </div>
  );

  const actionBar = (
    <>
      {isProductAdmin && tenants.length > 0 && (
        <Select value={tenantFilter} onChange={(e) => setTenantFilter(e.target.value)}>
          <option value="">전체 테넌트</option>
          {tenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </Select>
      )}
      {customers.length > 0 && searchBar}
      {canManageCustomers && (
        <Button color="primary" onClick={openAddModal} disabled={tenants.length === 0}
          title={tenants.length === 0 ? '먼저 테넌트를 등록해주세요' : undefined}>
          + 고객사 등록
        </Button>
      )}
    </>
  );

  return (
    <>
      {!embedded && (
        <Topbar title="고객사 관리" description="고객사(테넌트 하위 조직)를 관리합니다" />
      )}

      <div className={embedded ? '' : 'page-content'}>
        {embedded && (
          <div className="admin-panel-head">
            <PanelTitle title="고객사 관리" description="고객사(테넌트 하위 조직)를 관리합니다." />
            <div className="admin-panel-actions">{actionBar}</div>
          </div>
        )}

        <div className={embedded ? 'admin-section' : 'page-content-inner'}>
          {!embedded && (
            <div className="admin-panel-actions">{actionBar}</div>
          )}

          {loading ? (
            <div className="loading">로딩 중...</div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th><SortableTh sort={sort} col="name">이름</SortableTh></th>
                    <th><SortableTh sort={sort} col="code">코드</SortableTh></th>
                    <th><SortableTh sort={sort} col="tenantName">소속 테넌트</SortableTh></th>
                    <th>팀 수</th>
                    <th><SortableTh sort={sort} col="createdAt">등록일</SortableTh></th>
                    <th>작업</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="table-empty">
                        {searchQuery
                          ? `"${searchQuery}"에 대한 검색 결과가 없습니다.`
                          : tenants.length === 0
                            ? '먼저 테넌트 관리에서 테넌트를 등록해야 고객사를 등록할 수 있습니다.'
                            : '등록된 고객사가 없습니다. 우측 상단 "고객사 등록"으로 추가하세요.'}
                      </td>
                    </tr>
                  ) : (
                    sort.sortRows(filtered, (c, key) => c[key] || '').map((c) => (
                      <tr key={c.id}>
                        <td className="left">
                          {canManageCustomers ? (
                            <a href="#" className="table-link" onClick={(e) => { e.preventDefault(); openDetail(c); }}>
                              {highlightText(c.name)}
                            </a>
                          ) : (
                            <span>{highlightText(c.name)}</span>
                          )}
                        </td>
                        <td className="left">{highlightText(c.code)}</td>
                        <td>{c.tenantName || '-'}</td>
                        <td>
                          <button
                            className="table-link"
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent)', fontWeight: 600, padding: '2px 6px' }}
                            onClick={() => openTeamModal(c)}
                            title="팀 관리"
                          >
                            {teamCounts[c.id] ?? 0}
                          </button>
                        </td>
                        <td>{c.createdAt ? new Date(c.createdAt).toLocaleDateString() : '-'}</td>
                        <td>
                          {canManageCustomers ? (
                            <div className="user-actions">
                              <button className="icon-action-btn" onClick={() => openDetail(c)} title="수정"><EditIcon /></button>
                              <button className="icon-action-btn delete" onClick={() => setDeleteConfirm({ id: c.id, name: c.name })} title="삭제"><TrashIcon /></button>
                            </div>
                          ) : (
                            <span className="user-actions-empty">-</span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ── 고객사 상세/수정 모달 ─────────────────────────────── */}
      <Modal
        isOpen={!!selected}
        onClose={closeDetail}
        title="고객사 상세 정보"
        size="sm"
        footerRight={
          <>
            <Button color="gray" appearance="outline" onClick={closeDetail}>닫기</Button>
            <Button color="primary" onClick={handleSave} disabled={saving}>{saving ? '저장 중...' : '저장'}</Button>
          </>
        }
      >
        {selected && (
          <div className="form-grid">
            <div className="form-item">
              <div className="form-label">소속 테넌트</div>
              <Select value={editForm.tenantId} onChange={(e) => setEditForm({ ...editForm, tenantId: e.target.value })}>
                <option value="">테넌트 선택</option>
                {tenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </Select>
            </div>
            <div className="form-item">
              <div className="form-label">이름</div>
              <div className="input">
                <input className="input-control" type="text" value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
              </div>
            </div>
            <div className="form-item">
              <div className="form-label">코드</div>
              <div className="input">
                <input className="input-control" type="text" value={editForm.code}
                  onChange={(e) => setEditForm({ ...editForm, code: e.target.value })} />
              </div>
            </div>
            <div className="form-item">
              <div className="form-label">설명</div>
              <div className="input">
                <input className="input-control" type="text" value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} />
              </div>
            </div>
            <p className="user-detail-meta">
              ID {selected.id}<br />
              소속 테넌트 {selected.tenantName || selected.tenantId}<br />
              등록 {selected.createdAt ? new Date(selected.createdAt).toLocaleString() : '-'}
            </p>
          </div>
        )}
      </Modal>

      {/* ── 고객사 등록 모달 ──────────────────────────────────── */}
      <Modal
        isOpen={showAddModal}
        onClose={closeAddModal}
        title="고객사 등록"
        size="sm"
        footerRight={
          <>
            <Button color="gray" appearance="outline" onClick={closeAddModal}>닫기</Button>
            <Button color="primary" onClick={handleAdd} disabled={adding}>{adding ? '등록 중...' : '등록'}</Button>
          </>
        }
      >
        <div className="form-grid">
          <div className={`form-item${addErrors.has('tenantId') ? ' has-error' : ''}`}>
            <div className="form-label">소속 테넌트 <span className="required">*</span></div>
            <Select value={addForm.tenantId} onChange={(e) => { setAddForm({ ...addForm, tenantId: e.target.value }); clearAddError('tenantId'); }}>
              <option value="">테넌트 선택</option>
              {tenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </Select>
          </div>
          <div className="form-item">
            <div className="form-label">이름 <span className="required">*</span></div>
            <div className={`input${addErrors.has('name') ? ' has-error' : ''}`}>
              <input className="input-control" type="text" placeholder="고객사 이름"
                value={addForm.name} onChange={(e) => { setAddForm({ ...addForm, name: e.target.value }); clearAddError('name'); }} />
            </div>
          </div>
          <div className="form-item">
            <div className="form-label">코드 <span className="required">*</span></div>
            <div className={`input${addErrors.has('code') ? ' has-error' : ''}`}>
              <input className="input-control" type="text" placeholder="예: sample"
                value={addForm.code} onChange={(e) => { setAddForm({ ...addForm, code: e.target.value }); clearAddError('code'); }} />
            </div>
          </div>
          <div className="form-item">
            <div className="form-label">설명</div>
            <div className="input">
              <input className="input-control" type="text" placeholder="설명 (선택)"
                value={addForm.description} onChange={(e) => setAddForm({ ...addForm, description: e.target.value })} />
            </div>
          </div>
        </div>
      </Modal>

      {/* ── 고객사 삭제 확인 모달 ─────────────────────────────── */}
      <Modal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="고객사 삭제"
        size="sm"
        className="dialog--confirm"
        bodyClassName="dialog-body--compact"
        footerRight={
          <>
            <Button color="gray" appearance="outline" onClick={() => setDeleteConfirm(null)}>취소</Button>
            <Button color="error" onClick={confirmDelete} disabled={deleting}>삭제</Button>
          </>
        }
      >
        <p className="dialog-confirm-text">
          "{deleteConfirm?.name}" 고객사를 삭제하시겠습니까?<br />삭제 후 복구할 수 없습니다.
        </p>
      </Modal>

      {/* ── 팀 관리 모달 ─────────────────────────────────────── */}
      <Modal
        isOpen={!!teamModalCustomer}
        onClose={closeTeamModal}
        title={`팀 관리 — ${teamModalCustomer?.name || ''}`}
        size="md"
        footerRight={
          <>
            {canManageTeams && !showTeamAddForm && (
              <Button color="primary" onClick={() => { setShowTeamAddForm(true); setTeamAddErrors(new Set()); }}>
                + 팀 추가
              </Button>
            )}
            <Button color="gray" appearance="outline" onClick={closeTeamModal}>닫기</Button>
          </>
        }
      >
        <div style={{ minHeight: 200 }}>
          {/* 팀 등록 인라인 폼 */}
          {showTeamAddForm && (
            <div style={{ background: 'var(--bg-subtle, #f9fafb)', border: '1px solid var(--border)', borderRadius: 8, padding: '14px 16px', marginBottom: 16 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                <div style={{ flex: 1 }}>
                  <div className="form-label" style={{ marginBottom: 4 }}>팀 이름 <span className="required">*</span></div>
                  <div className={`input${teamAddErrors.has('name') ? ' has-error' : ''}`}>
                    <input
                      className="input-control"
                      type="text"
                      placeholder="팀 이름 입력"
                      value={teamAddForm.name}
                      onChange={(e) => { setTeamAddForm({ ...teamAddForm, name: e.target.value }); setTeamAddErrors(new Set()); }}
                      autoFocus
                    />
                  </div>
                </div>
                <div style={{ flex: 1 }}>
                  <div className="form-label" style={{ marginBottom: 4 }}>설명</div>
                  <div className="input">
                    <input
                      className="input-control"
                      type="text"
                      placeholder="설명 (선택)"
                      value={teamAddForm.description}
                      onChange={(e) => setTeamAddForm({ ...teamAddForm, description: e.target.value })}
                    />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, paddingBottom: 1 }}>
                  <Button color="primary" onClick={handleTeamAdd} disabled={teamAdding}>
                    {teamAdding ? '등록 중...' : '등록'}
                  </Button>
                  <Button color="gray" appearance="outline" onClick={() => { setShowTeamAddForm(false); setTeamAddForm({ name: '', description: '' }); }}>
                    취소
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* 팀 목록 */}
          {teamsLoading ? (
            <div className="loading" style={{ padding: '40px 0', textAlign: 'center' }}>로딩 중...</div>
          ) : teams.length === 0 ? (
            <div className="table-empty" style={{ padding: '40px 0', textAlign: 'center', color: 'var(--text-muted)' }}>
              등록된 팀이 없습니다.{canManageTeams && ' 우측 상단 "팀 추가"로 팀을 만들어보세요.'}
            </div>
          ) : (
            <div className="table-wrap" style={{ marginTop: 0 }}>
              <table>
                <thead>
                  <tr>
                    <th className="left">팀 이름</th>
                    <th className="left">설명</th>
                    <th>등록일</th>
                    {canManageTeams && <th>작업</th>}
                  </tr>
                </thead>
                <tbody>
                  {teams.map((team) => (
                    <tr key={team.id}>
                      {editingTeamId === team.id ? (
                        <>
                          <td>
                            <div className="input">
                              <input
                                className="input-control"
                                type="text"
                                value={teamEditForm.name}
                                onChange={(e) => setTeamEditForm({ ...teamEditForm, name: e.target.value })}
                                autoFocus
                              />
                            </div>
                          </td>
                          <td>
                            <div className="input">
                              <input
                                className="input-control"
                                type="text"
                                placeholder="설명"
                                value={teamEditForm.description}
                                onChange={(e) => setTeamEditForm({ ...teamEditForm, description: e.target.value })}
                              />
                            </div>
                          </td>
                          <td>{team.createdAt ? new Date(team.createdAt).toLocaleDateString() : '-'}</td>
                          <td>
                            <div className="user-actions">
                              <Button color="primary" onClick={() => handleTeamSave(team.id)} disabled={teamSaving} style={{ padding: '4px 12px', fontSize: 13 }}>
                                {teamSaving ? '저장...' : '저장'}
                              </Button>
                              <Button color="gray" appearance="outline" onClick={cancelTeamEdit} style={{ padding: '4px 10px', fontSize: 13 }}>
                                취소
                              </Button>
                            </div>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="left" style={{ fontWeight: 500 }}>{team.name}</td>
                          <td className="left" style={{ color: 'var(--text-muted)' }}>{team.description || '-'}</td>
                          <td>{team.createdAt ? new Date(team.createdAt).toLocaleDateString() : '-'}</td>
                          {canManageTeams && (
                            <td>
                              <div className="user-actions">
                                <button className="icon-action-btn" onClick={() => startTeamEdit(team)} title="수정"><EditIcon /></button>
                                <button className="icon-action-btn delete" onClick={() => handleTeamDelete(team)} title="삭제"><TrashIcon /></button>
                              </div>
                            </td>
                          )}
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Modal>

      {/* ── 팀 삭제 확인 모달 ────────────────────────────────── */}
      <Modal
        isOpen={!!teamDeleteConfirm}
        onClose={() => setTeamDeleteConfirm(null)}
        title="팀 삭제"
        size="sm"
        className="dialog--confirm"
        bodyClassName="dialog-body--compact"
        footerRight={
          <>
            <Button color="gray" appearance="outline" onClick={() => setTeamDeleteConfirm(null)}>취소</Button>
            <Button color="error" onClick={confirmTeamDelete} disabled={teamDeleting}>삭제</Button>
          </>
        }
      >
        <p className="dialog-confirm-text">
          "{teamDeleteConfirm?.name}" 팀을 삭제하시겠습니까?<br />삭제 후 복구할 수 없습니다.
        </p>
      </Modal>
    </>
  );
}

export default CustomersPage;
